import { uuidv7 } from 'uuidv7';
import type { InterventionUse, Session, SessionAnalysis } from '@/domain/schema/v1';
import { useAutosave } from '../../hooks/useAutosave';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { ItemsEditor, LinesInput, SaveIndicator, TextArea } from '../../components/fields';

/**
 * 회기 분석 (CLINICAL_DATA_MODEL §5). V1에서는 상담자가 직접 입력하거나 (M5) AI 결과를 가져온다.
 * 정서·인지·행동·관계는 기본이 '가설'이다.
 */
export function AnalysisSection({ session }: { session: Session }) {
  const svc = useService();
  const { data } = useLoad(() => svc.ensureAnalysis(session.id), [svc, session.id]);
  if (!data) return null;
  return <AnalysisEditor analysis={data} session={session} />;
}

function AnalysisEditor({ analysis, session }: { analysis: SessionAnalysis; session: Session }) {
  const svc = useService();
  const a = useAutosave<SessionAnalysis>(analysis, (patch, rev) => svc.updateAnalysis(analysis.id, patch, rev));
  const s = useAutosave<Session>(session, (patch, rev) => svc.updateSession(session.id, patch, rev));
  const v = a.value!;
  return (
    <div className="card">
      <div className="card-head">
        <h2>회기 분석</h2>
        <SaveIndicator status={a.status === 'idle' ? s.status : a.status} error={a.error ?? s.error} />
      </div>
      <LinesInput
        label="주요 주제"
        value={s.value?.primaryThemes}
        onChange={(x) => s.set('primaryThemes', x.length ? x : undefined)}
      />
      <TextArea
        label="회기 핵심 요약"
        value={v.sessionSummary}
        onChange={(x) => a.set('sessionSummary', x || undefined)}
      />
      <LinesInput
        label="주요 사건"
        value={v.keyEvents}
        onChange={(x) => a.set('keyEvents', x.length ? x : undefined)}
      />
      <LinesInput
        label="최근 변화"
        value={v.notableChanges}
        onChange={(x) => a.set('notableChanges', x.length ? x : undefined)}
      />
      <details>
        <summary>정서 · 인지 · 행동 · 관계</summary>
        <ItemsEditor label="정서" value={v.emotions} onChange={(x) => a.set('emotions', x)} />
        <ItemsEditor label="인지" value={v.cognitions} onChange={(x) => a.set('cognitions', x)} />
        <ItemsEditor label="행동" value={v.behaviors} onChange={(x) => a.set('behaviors', x)} />
        <ItemsEditor
          label="관계 패턴"
          value={v.relationshipPatterns}
          onChange={(x) => a.set('relationshipPatterns', x)}
        />
      </details>
      <InterventionsEditor value={v.interventions} onChange={(x) => a.set('interventions', x)} />
    </div>
  );
}

function InterventionsEditor({
  value,
  onChange,
}: {
  value: InterventionUse[] | undefined;
  onChange: (v: InterventionUse[]) => void;
}) {
  const items = value ?? [];
  const up = (i: number, patch: Partial<InterventionUse>) =>
    onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="field">
      <div className="field-label">
        <span>상담자 개입</span>
      </div>
      <ul className="items">
        {items.map((it, i) => (
          <li key={it.id} className="stack item-block">
            <input
              aria-label={`개입 ${i + 1} 이름`}
              placeholder="개입 (예: 감정 반영)"
              value={it.interventionName}
              onChange={(e) => up(i, { interventionName: e.target.value })}
            />
            <input
              aria-label={`개입 ${i + 1} 이론`}
              placeholder="이론적 배경 (선택)"
              value={it.theoreticalOrientation ?? ''}
              onChange={(e) => up(i, { theoreticalOrientation: e.target.value || undefined })}
            />
            <input
              aria-label={`개입 ${i + 1} 내담자 반응`}
              placeholder="내담자 반응 (선택)"
              value={it.clientResponse ?? ''}
              onChange={(e) => up(i, { clientResponse: e.target.value || undefined })}
            />
            <label className="check">
              <input
                type="checkbox"
                checked={!!it.followUpNeeded}
                onChange={(e) => up(i, { followUpNeeded: e.target.checked })}
              />{' '}
              다음 회기에 이어서
            </label>
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}>
              삭제
            </button>
          </li>
        ))}
      </ul>
      <button type="button" onClick={() => onChange([...items, { id: uuidv7(), interventionName: '개입' }])}>
        + 개입 추가
      </button>
    </div>
  );
}
